import { useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/lib/supabase";

interface ChargeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onChargeComplete?: () => void;
}

const PACKAGES = [
  { id: "p1", amount: 10000, label: "₩10,000", sms: "500건", popular: false },
  { id: "p2", amount: 30000, label: "₩30,000", sms: "1,500건", popular: false },
  { id: "p3", amount: 50000, label: "₩50,000", sms: "2,500건", popular: true },
  { id: "p4", amount: 100000, label: "₩100,000", sms: "5,000건", popular: false },
  { id: "p5", amount: 200000, label: "₩200,000", sms: "10,000건", popular: false },
  { id: "p6", amount: 500000, label: "₩500,000", sms: "25,000건", popular: false },
];

type Step = "select" | "confirm" | "complete";

export default function ChargeModal({ isOpen, onClose, onChargeComplete }: ChargeModalProps) {
  const { user, session } = useAuth();
  const [step, setStep] = useState<Step>("select");
  const [selectedPackage, setSelectedPackage] = useState<string>("p3");
  const [customAmount, setCustomAmount] = useState<string>("");
  const [useCustom, setUseCustom] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [requestedAmount, setRequestedAmount] = useState(0);
  const [error, setError] = useState<string | null>(null);

  if (!isOpen) return null;

  const getSelectedAmount = () => {
    if (useCustom) {
      const val = parseInt(customAmount.replace(/,/g, ""), 10);
      return isNaN(val) ? 0 : val;
    }
    return PACKAGES.find((p) => p.id === selectedPackage)?.amount ?? 0;
  };

  const getFinalAmount = () => getSelectedAmount();

  const getSmsCount = () => {
    return Math.floor(getFinalAmount() / 20).toLocaleString();
  };

  const handleCustomAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value.replace(/[^0-9]/g, "");
    const num = parseInt(raw, 10);
    if (!isNaN(num)) {
      setCustomAmount(num.toLocaleString());
    } else {
      setCustomAmount("");
    }
  };

  const handleNext = async () => {
    if (step === "select") {
      const amount = getSelectedAmount();
      if (amount < 1000) return;
      setStep("confirm");
    } else if (step === "confirm") {
      if (!agreeTerms || !user || !session) return;
      setIsProcessing(true);
      setError(null);
      try {
        const baseAmount = getSelectedAmount();
        const description = `무통장 입금 ₩${baseAmount.toLocaleString()} 충전`;

        const { data, error: fnError } = await supabase.functions.invoke("create-charge-request", {
          body: {
            amount: baseAmount,
            bonus_amount: 0,
            description,
            user_id: user.id,
          },
        });

        if (fnError || data?.error) {
          setError(data?.error || fnError?.message || "충전 신청 중 오류가 발생했습니다");
          setIsProcessing(false);
          return;
        }

        setRequestedAmount(baseAmount);
        onChargeComplete?.();
        setStep("complete");
      } catch (err) {
        setError("네트워크 오류가 발생했습니다");
      } finally {
        setIsProcessing(false);
      }
    }
  };

  const handleClose = () => {
    setStep("select");
    setSelectedPackage("p3");
    setCustomAmount("");
    setUseCustom(false);
    setAgreeTerms(false);
    setIsProcessing(false);
    setError(null);
    setRequestedAmount(0);
    onClose();
  };

  const canProceed = () => {
    if (step === "select") {
      return useCustom ? parseInt(customAmount.replace(/,/g, ""), 10) >= 1000 : !!selectedPackage;
    }
    if (step === "confirm") return agreeTerms;
    return true;
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="relative w-full max-w-lg bg-[#0f0f1e] border border-white/[0.1] rounded-2xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-white/[0.07]">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 flex items-center justify-center rounded-xl bg-gradient-to-br from-amber-500 to-orange-500">
              <i className="ri-wallet-3-line text-white text-base"></i>
            </div>
            <div>
              <h2 className="text-white font-semibold text-base">잔액 충전</h2>
              <p className="text-gray-500 text-xs">
                {step === "select" && "충전 금액을 선택하세요"}
                {step === "confirm" && "무통장 입금 안내"}
                {step === "complete" && "신청이 완료되었습니다"}
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="w-8 h-8 flex items-center justify-center text-gray-500 hover:text-white cursor-pointer transition-colors rounded-lg hover:bg-white/[0.06]"
          >
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        {/* Step indicator */}
        {step !== "complete" && (
          <div className="flex items-center gap-2 px-6 pt-4">
            {["select", "confirm"].map((s, idx) => (
              <div key={s} className="flex items-center gap-2">
                <div
                  className={`w-6 h-6 flex items-center justify-center rounded-full text-xs font-semibold transition-all ${
                    step === s
                      ? "bg-gradient-to-br from-amber-500 to-orange-500 text-white"
                      : idx < ["select", "confirm"].indexOf(step)
                      ? "bg-emerald-500/20 text-emerald-400 border border-emerald-500/30"
                      : "bg-white/[0.06] text-gray-500"
                  }`}
                >
                  {idx < ["select", "confirm"].indexOf(step) ? (
                    <i className="ri-check-line text-xs"></i>
                  ) : (
                    idx + 1
                  )}
                </div>
                <span
                  className={`text-xs whitespace-nowrap ${
                    step === s ? "text-white font-medium" : "text-gray-500"
                  }`}
                >
                  {s === "select" ? "금액 선택" : "입금 안내"}
                </span>
                {idx < 1 && <div className="w-8 h-px bg-white/[0.1] mx-1"></div>}
              </div>
            ))}
          </div>
        )}

        {/* Body */}
        <div className="px-6 py-5">
          {/* STEP 1: Select amount */}
          {step === "select" && (
            <div className="space-y-4">
              {/* Package grid */}
              <div>
                <div className="grid grid-cols-3 gap-2">
                  {PACKAGES.map((pkg) => (
                    <button
                      key={pkg.id}
                      onClick={() => { setSelectedPackage(pkg.id); setUseCustom(false); }}
                      className={`relative flex flex-col items-center justify-center p-3 rounded-xl border transition-all cursor-pointer ${
                        !useCustom && selectedPackage === pkg.id
                          ? "border-amber-500/60 bg-amber-500/10"
                          : "border-white/[0.07] bg-white/[0.02] hover:border-white/[0.15] hover:bg-white/[0.04]"
                      }`}
                    >
                      {pkg.popular && (
                        <span className="absolute -top-2 left-1/2 -translate-x-1/2 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-[10px] font-semibold px-2 py-0.5 rounded-full whitespace-nowrap">
                          인기
                        </span>
                      )}
                      <span className={`text-sm font-bold whitespace-nowrap ${!useCustom && selectedPackage === pkg.id ? "text-white" : "text-gray-300"}`}>
                        {pkg.label}
                      </span>
                      <span className="text-gray-500 text-[11px] mt-0.5 whitespace-nowrap">{pkg.sms}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Custom amount */}
              <div>
                <button
                  onClick={() => setUseCustom(!useCustom)}
                  className="flex items-center gap-2 text-xs text-gray-400 hover:text-white cursor-pointer transition-colors mb-2"
                >
                  <div className={`w-4 h-4 flex items-center justify-center rounded border transition-all ${useCustom ? "border-amber-500 bg-amber-500" : "border-white/20"}`}>
                    {useCustom && <i className="ri-check-line text-white text-[10px]"></i>}
                  </div>
                  직접 입력
                </button>
                {useCustom && (
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">₩</span>
                    <input
                      type="text"
                      value={customAmount}
                      onChange={handleCustomAmountChange}
                      placeholder="최소 1,000원"
                      className="w-full bg-white/[0.04] border border-white/[0.1] focus:border-amber-500/60 rounded-xl pl-8 pr-4 py-3 text-white text-sm outline-none transition-colors placeholder:text-gray-600"
                    />
                  </div>
                )}
              </div>

              {/* Summary */}
              {((!useCustom && selectedPackage) || (useCustom && customAmount)) && (
                <div className="bg-white/[0.03] border border-white/[0.07] rounded-xl p-4 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">충전 금액</span>
                    <span className="text-white font-medium">₩{getSelectedAmount().toLocaleString()}</span>
                  </div>
                  <div className="h-px bg-white/[0.07]"></div>
                  <div className="flex justify-between">
                    <span className="text-white font-semibold text-sm">최종 충전액</span>
                    <span className="text-amber-400 font-bold text-base">₩{getFinalAmount().toLocaleString()}</span>
                  </div>
                  <p className="text-gray-500 text-xs text-right">≈ SMS {getSmsCount()}건 발송 가능</p>
                </div>
              )}
            </div>
          )}

          {/* STEP 2: Confirm bank transfer */}
          {step === "confirm" && (
            <div className="space-y-4">
              {/* Amount summary */}
              <div className="bg-gradient-to-r from-amber-500/10 to-orange-500/10 border border-amber-500/20 rounded-xl p-4 flex items-center justify-between">
                <div>
                  <p className="text-gray-400 text-xs">입금 예정 금액</p>
                  <p className="text-white font-bold text-xl mt-0.5">₩{getSelectedAmount().toLocaleString()}</p>
                </div>
              </div>

              {/* Contact info */}
              <div className="bg-white/[0.03] border border-white/[0.08] rounded-xl p-4 space-y-3">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-amber-500/10">
                    <i className="ri-telegram-line text-amber-400 text-base"></i>
                  </div>
                  <p className="text-white font-semibold text-sm">충전 신청 안내</p>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#0088cc]/10 shrink-0">
                      <i className="ri-telegram-fill text-[#0088cc] text-lg"></i>
                    </div>
                    <div>
                      <span className="text-white text-sm font-medium">텔레그램</span>
                      <span className="text-gray-500 text-xs ml-2">@signal_sms</span>
                    </div>
                  </div>
                  <div className="h-px bg-white/[0.07]"></div>
                  <div className="flex items-start gap-2">
                    <i className="ri-information-line text-amber-400 text-sm mt-0.5 shrink-0"></i>
                    <p className="text-gray-400 text-xs leading-relaxed">
                      충전을 원하시는 금액과 가입 이메일을 함께 텔레그램으로 문의해 주세요.
                      입금 확인 후 관리자가 승인하면 잔액이 충전됩니다.
                      평일 기준 1~2시간 내 승인 처리됩니다.
                    </p>
                  </div>
                </div>
              </div>

              {/* Terms */}
              <button
                onClick={() => setAgreeTerms(!agreeTerms)}
                className="flex items-start gap-2.5 cursor-pointer group w-full text-left"
              >
                <div className={`w-4 h-4 flex items-center justify-center rounded border transition-all mt-0.5 shrink-0 ${agreeTerms ? "border-amber-500 bg-amber-500" : "border-white/20 group-hover:border-white/40"}`}>
                  {agreeTerms && <i className="ri-check-line text-white text-[10px]"></i>}
                </div>
                <p className="text-gray-400 text-xs leading-relaxed">
                  충전 신청 시 <span className="text-amber-400 underline cursor-pointer">이용약관</span> 및{" "}
                  <span className="text-amber-400 underline cursor-pointer">환불 정책</span>에 동의하는 것으로 간주됩니다.
                  무통장 입금 후 관리자 승인이 완료되어야 충전이 확정됩니다.
                </p>
              </button>

              {error && (
                <div className="flex items-center gap-2 text-red-400 text-xs bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">
                  <i className="ri-error-warning-line text-sm"></i>
                  {error}
                </div>
              )}
            </div>
          )}

          {/* STEP 3: Complete */}
          {step === "complete" && (
            <div className="flex flex-col items-center py-6 text-center">
              <div className="relative mb-5">
                <div className="w-20 h-20 flex items-center justify-center rounded-full bg-gradient-to-br from-amber-500/20 to-orange-500/20 border border-amber-500/30">
                  <i className="ri-time-line text-amber-400 text-4xl"></i>
                </div>
                <div className="absolute inset-0 rounded-full bg-amber-500/10 animate-ping"></div>
              </div>
              <h3 className="text-white font-bold text-xl mb-1">충전 신청 완료!</h3>
              <p className="text-gray-400 text-sm mb-5">
                <span className="text-amber-400 font-semibold">₩{requestedAmount.toLocaleString()}</span> 충전 신청이 접수되었습니다.
              </p>
              <div className="w-full bg-white/[0.03] border border-white/[0.07] rounded-xl p-4 space-y-2.5 text-left mb-5">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-400">신청 금액</span>
                  <span className="text-white">₩{getSelectedAmount().toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-400">결제 수단</span>
                  <span className="text-white">무통장 입금</span>
                </div>
                <div className="h-px bg-white/[0.07]"></div>
                <div className="flex justify-between">
                  <span className="text-amber-400 text-sm font-medium">처리 상태</span>
                  <span className="text-amber-400 font-bold text-sm">승인 대기중</span>
                </div>
              </div>
              <div className="flex items-start gap-2 bg-amber-500/5 border border-amber-500/15 rounded-xl px-4 py-3 w-full mb-5">
                <i className="ri-information-line text-amber-400 text-sm mt-0.5 shrink-0"></i>
                <p className="text-gray-400 text-xs leading-relaxed text-left">
                  입금 확인 후 관리자가 승인하면 잔액이 충전됩니다.
                  승인 상태는 설정 &gt; 크레딧 내역에서 확인할 수 있습니다.
                </p>
              </div>
              <button
                onClick={handleClose}
                className="w-full bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-500 hover:to-orange-500 text-white py-3 rounded-xl font-semibold text-sm cursor-pointer transition-all hover:scale-[1.02] whitespace-nowrap"
              >
                확인
              </button>
            </div>
          )}
        </div>

        {/* Footer buttons */}
        {step !== "complete" && (
          <div className="px-6 pb-5 flex gap-3">
            {step === "confirm" && (
              <button
                onClick={() => setStep("select")}
                className="flex-1 py-3 rounded-xl border border-white/[0.1] text-gray-400 hover:text-white hover:border-white/[0.2] text-sm font-medium cursor-pointer transition-all whitespace-nowrap"
              >
                이전
              </button>
            )}
            <button
              onClick={handleNext}
              disabled={!canProceed() || isProcessing}
              className={`flex-1 py-3 rounded-xl text-sm font-semibold transition-all whitespace-nowrap flex items-center justify-center gap-2 ${
                canProceed() && !isProcessing
                  ? "bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-500 hover:to-orange-500 text-white cursor-pointer hover:scale-[1.02]"
                  : "bg-white/[0.05] text-gray-600 cursor-not-allowed"
              }`}
            >
              {isProcessing ? (
                <>
                  <i className="ri-loader-4-line animate-spin text-base"></i>
                  신청 처리 중...
                </>
              ) : step === "select" ? (
                <>
                  다음 단계
                  <i className="ri-arrow-right-line text-base"></i>
                </>
              ) : (
                <>
                  <i className="ri-file-list-3-line text-base"></i>
                  충전 신청하기
                </>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}